import axios from "axios";
import { useEffect, useState } from "react";
import { FaCheck, FaEdit, FaSave, FaTimes, FaUndo, FaUserMinus, FaUserPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function AssignPickup() {
   const navigate = useNavigate();
   const { id } = useParams();
   const [pickup, setPickup] = useState(null);
   const [drivers, setDrivers] = useState([]);
   const [selectedDriver, setSelectedDriver] = useState("");
   const [editMode, setEditMode] = useState(false);
   const [form, setForm] = useState({});
   const [barcodeOptions, setBarcodeOptions] = useState([]);   // 🔹 available unused barcodes
   const [selectedBarcodes, setSelectedBarcodes] = useState([]); // 🔹 selected by admin

   const generateTimeSlots = () => {
      const slots = [];
      for (let hour = 8; hour <= 18; hour++) {
         const minuteSteps = hour === 18 ? [0] : [0, 15, 30, 45];
         for (let minutes of minuteSteps) {
            const time = new Date();
            time.setHours(hour, minutes, 0, 0);
            const formatted = time.toLocaleTimeString('en-US', {
               hour: '2-digit',
               minute: '2-digit',
               hour12: true,
            });
            slots.push(formatted);
         }
      }
      return slots;
   };

   const to24HourFormat = (time12h) => {
      const [time, modifier] = time12h.split(' ');
      let [hours, minutes] = time.split(':');

      hours = parseInt(hours, 10);
      if (modifier === 'PM' && hours < 12) hours += 12;
      if (modifier === 'AM' && hours === 12) hours = 0;

      return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
   };

   useEffect(() => {
      const fetchData = async () => {
         try {
            const pickupRes = await axios.get(`/auth/redem-pickup-requests/${id}`);
            const pickupData = pickupRes.data;

            // Now fetch drivers based on the pickup's rc_id
            const driversRes = await axios.get(`/auth/redem-pickup/drivers/by-rc`, {
               params: { rc_id: pickupData.rc_id }
            });

            const driverList = driversRes.data;
            setPickup(pickupData);
            setForm(pickupData);
            setSelectedDriver(pickupData.driver_assigned || "");

            // If assigned driver not in list, add manually
            if (
               pickupData.driver_assigned &&
               !driverList.find(d => d.accountNumber === pickupData.driver_assigned)
            ) {
               driverList.unshift({
                  accountNumber: pickupData.driver_assigned,
                  name: "Not yet assigned"
               });
            }

            setDrivers(driverList);

         } catch (err) {
            console.error("Error fetching data:", err);
         }
      };

      fetchData();
   }, [id]);

   const handleAssign = async () => {
      try {
         await axios.put(`/auth/redem-pickup-requests/${id}/assign-driver`, {
            driverAccountNumber: selectedDriver,
         });
         toast.success("Driver assigned successfully!");
         setTimeout(() => navigate("/redem-pickuprequests"), 1500);
      } catch (err) {
         toast.error("Assignment failed.");
      }
   };

   const handleUnassign = async () => {
      try {
         await axios.put(`/auth/redem-pickup-requests/${id}/unassign-driver`);
         toast.success("Driver unassigned.");
         setSelectedDriver("");
         setPickup(prev => ({ ...prev, driver_assigned: null }));
      } catch (err) {
         toast.error("Unassign failed.");
      }
   };

   const handleCancel = async () => {
      try {
         await axios.put(`/auth/redem-pickup-requests/${id}/cancel`);
         toast.success("Pickup canceled.");
         setPickup(prev => ({ ...prev, pickup_status: "cancelled" }));
      } catch (err) {
         toast.error("Cancel failed.");
      }
   };

   // 🔹 Try revive
   const handleRevive = async () => {
      try {
         const res = await axios.put(`/auth/redem-pickup-requests/${id}/revive`);

         if (res.data.needsSelection) {
            toast.info("Some barcodes were invalid. Please select from available unused barcodes.");
            setBarcodeOptions(res.data.availableBarcodes);
         } else {
            toast.success(res.data.message || "Pickup revived.");
            setPickup(prev => ({ ...prev, pickup_status: "pending", barcode: res.data.barcodes?.join(",") || prev.barcode }));
         }
      } catch (err) {
         const msg = err.response?.data?.error || "Revive failed.";
         toast.error(msg);
      }
   };

   // 🔹 Confirm revive with selected barcodes
   const handleConfirmRevive = async () => {
      try {
         const res = await axios.put(`/auth/redem-pickup-requests/${id}/revive`, {
            selectedBarcodes
         });

         toast.success(res.data.message || "Pickup revived with selected barcodes.");
         setPickup(prev => ({ ...prev, pickup_status: "pending", barcode: res.data.barcodes?.join(",") || prev.barcode }));
         setBarcodeOptions([]);
         setSelectedBarcodes([]);
      } catch (err) {
         const msg = err.response?.data?.error || "Confirm revive failed.";
         toast.error(msg);
      }
   };

   const handleEdit = async () => {
      try {
         const formattedData = {
            ...form,
            pickup_date: new Date(form.pickup_date).toISOString().split('T')[0], // still needs 'YYYY-MM-DD'
            pickup_time: form.pickup_time, // keep as '11:00 AM', no conversion
         };

         await axios.put(`/auth/redem-pickup-requests/${id}/edit`, formattedData);

         toast.success("Pickup details updated.");
         setEditMode(false);
         setPickup(formattedData);
      } catch (err) {
         toast.error("Update failed.");
      }
   };

   if (!pickup) return <div className="p-6">Loading...</div>;

   return (
      <section className="md:flex items-center justify-center px-6 py-12 bg-gray-50 min-h-screen">
         <ToastContainer />
         <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-8 py-6 bg-green-100 border-b border-green-200 flex justify-between items-center">
               <h2 className="text-3xl font-bold text-green-800">Assign / Manage Pickup</h2>
               <span className={`text-sm px-3 py-1 rounded-full font-medium ${pickup.pickup_status === 'cancelled'
                  ? 'bg-red-200 text-red-700'
                  : pickup.pickup_status === 'assigned'
                     ? 'bg-blue-200 text-blue-700'
                     : 'bg-yellow-200 text-yellow-800'
                  }`}>
                  {pickup.pickup_status.toUpperCase()}
               </span>
            </div>

            <div className="px-10 py-8 space-y-6">
               {/* Pickup Info */}
               <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Pickup Details</h3>
                  {editMode ? (
                     <div className="grid sm:grid-cols-2 gap-4">
                        <input className="border p-2 rounded" placeholder="Street Address" value={form.street_address} onChange={(e) => setForm({ ...form, street_address: e.target.value })} />
                        <input className="border p-2 rounded" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                        <input
                           className="border p-2 rounded"
                           type="date"
                           value={form.pickup_date?.split('T')[0]}
                           onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                           min={new Date().toISOString().split('T')[0]}
                        />

                        <select
                           className="border p-2 rounded w-full"
                           value={form.pickup_time}
                           onChange={(e) => setForm({ ...form, pickup_time: e.target.value })}
                        >
                           {generateTimeSlots().map((slot, index) => (
                              <option key={index} value={slot}>
                                 {slot}
                              </option>
                           ))}
                        </select>

                        <textarea className="border p-2 rounded col-span-full" placeholder="Driver Instructions" value={form.driver_instructions || ""} onChange={(e) => setForm({ ...form, driver_instructions: e.target.value })}></textarea>
                     </div>
                  ) : (
                     <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-4 text-sm text-gray-700">
                        <div><dt className="font-semibold">Customer</dt><dd>{pickup.first_name} {pickup.last_name}</dd></div>
                        <div><dt className="font-semibold">Phone</dt><dd>{pickup.phone}</dd></div>
                        <div><dt className="font-semibold">Address</dt><dd>{pickup.street_address}, {pickup.city}</dd></div>
                        <div><dt className="font-semibold">Barcode</dt><dd>{pickup.barcode}</dd></div>
                        <div><dt className="font-semibold">Schedule</dt><dd>{pickup.pickup_date?.split('T')[0]} at {pickup.pickup_time}</dd></div>
                        <div className="sm:col-span-2"><dt className="font-semibold">Instructions</dt><dd>{pickup.driver_instructions || "N/A"}</dd></div>
                     </dl>
                  )}
               </div>

               {/* Driver Assignment */}
               <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Assign Driver</h3>
                  <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} className="w-full border p-2 rounded bg-white shadow-sm">
                     <option value="">Choose Driver</option>
                     {drivers.map(d => (
                        <option key={d.accountNumber} value={d.accountNumber}>{d.name} - {d.accountNumber}</option>
                     ))}
                  </select>
               </div>

               {/* 🔹 Barcode Selection UI */}
               {barcodeOptions.length > 0 && (
                  <div className="p-4 border rounded bg-gray-50">
                     <h3 className="font-semibold mb-2">Select Barcodes to Revive</h3>
                     <div className="grid grid-cols-2 gap-2">
                        {barcodeOptions.map(bc => (
                           <label key={bc} className="flex items-center gap-2 border p-2 rounded">
                              <input
                                 type="checkbox"
                                 value={bc}
                                 onChange={(e) => {
                                    if (e.target.checked) {
                                       setSelectedBarcodes(prev => [...prev, bc]);
                                    } else {
                                       setSelectedBarcodes(prev => prev.filter(b => b !== bc));
                                    }
                                 }}
                              />
                              {bc}
                           </label>
                        ))}
                     </div>
                     <button
                        onClick={handleConfirmRevive}
                        disabled={selectedBarcodes.length === 0}
                        className="mt-4 px-4 py-2 bg-green-600 text-white rounded flex items-center gap-2"
                     >
                        <FaCheck /> Confirm Revive
                     </button>
                  </div>
               )}

               {/* Action Buttons */}
               <div className="flex flex-wrap gap-4 border-t pt-6">
                  <button onClick={handleAssign} disabled={!selectedDriver} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"><FaUserPlus /> Assign</button>
                  <button onClick={handleUnassign} className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"><FaUserMinus /> Unassign</button>
                  <button onClick={() => setEditMode(!editMode)} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"><FaEdit /> {editMode ? "Cancel Edit" : "Edit"}</button>
                  {editMode && (
                     <button onClick={handleEdit} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"><FaSave /> Save</button>
                  )}
                  <button onClick={handleCancel} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"><FaTimes /> Cancel Pickup</button>
                  <button
                     onClick={handleRevive}
                     disabled={pickup.pickup_status !== "cancelled"}
                     className={`flex items-center gap-2 px-4 py-2 rounded text-white 
      ${pickup.pickup_status === "cancelled"
                           ? "bg-gray-700 hover:bg-gray-800"
                           : "bg-gray-400 cursor-not-allowed"
                        }`}
                  >
                     <FaUndo /> Revive Pickup
                  </button>

                  <button onClick={() => navigate("/redem-pickuprequests")} className="ml-auto px-4 py-2 border border-gray-400 text-gray-600 rounded hover:bg-gray-200">Back</button>
               </div>
            </div>
         </div>
      </section>
   );
}
